import { BulkStore } from '../types'

export type StoreEligibilityReason =
  | 'available'
  | 'pending-month-selection'
  | 'no-sellers'
  | 'already-has-goal'
  | 'missing-last-active-goal'
  | 'missing-availability'

export interface StoreEligibility {
  canSelect: boolean
  canSubmit: boolean
  reason: StoreEligibilityReason
}

export interface StoreSelectionRow {
  disabled: boolean
  eligibility: StoreEligibility
  status: string
  store: BulkStore
}

interface StoreEligibilityOptions {
  hasSelectedMonth?: boolean
}

export const getStoreEligibility = (
  store: BulkStore | undefined,
  options: StoreEligibilityOptions = {}
): StoreEligibility => {
  const hasSelectedMonth = options.hasSelectedMonth ?? true

  if (!store) return buildEligibility('missing-availability')
  if (!hasSelectedMonth) return buildEligibility('pending-month-selection')
  if (typeof store.sellers !== 'number') return buildEligibility('missing-availability')
  if (store.sellers === 0) return buildEligibility('no-sellers')

  if (typeof store.hasGoal !== 'boolean' || typeof store.hasLastActiveGoal !== 'boolean') {
    return buildEligibility('missing-availability')
  }
  if (store.hasGoal) return buildEligibility('already-has-goal')
  if (!store.hasLastActiveGoal) return buildEligibility('missing-last-active-goal')
  return buildEligibility('available')
}

export const buildStoreSelectionRows = (
  stores: BulkStore[],
  options: StoreEligibilityOptions = {}
): StoreSelectionRow[] => stores.map(store => {
  const eligibility = getStoreEligibility(store, options)

  return {
    disabled: !eligibility.canSelect,
    eligibility,
    status: getStoreEligibilityStatusLabel(eligibility.reason),
    store
  }
})

export const getStoreEligibilityStatusLabel = (
  reason: StoreEligibilityReason
) => {
  const labels: Record<StoreEligibilityReason, string> = {
    'already-has-goal': 'Já possui meta',
    available: 'Disponível',
    'missing-availability': 'Disponibilidade não validada',
    'missing-last-active-goal': 'Sem meta ativa anterior',
    'no-sellers': 'Sem vendedores',
    'pending-month-selection': 'Selecione um mês'
  }

  return labels[reason]
}

export const getStoreEligibilityFailureMessage = (
  reason: StoreEligibilityReason
) => {
  if (reason === 'no-sellers') return 'A loja não possui vendedores para criar a meta.'
  if (reason === 'already-has-goal') return 'A loja já possui meta cadastrada para este mês.'
  if (reason === 'missing-last-active-goal') return 'A loja não possui meta ativa anterior para copiar as configurações.'
  if (reason === 'missing-availability') return 'Não foi possível revalidar a disponibilidade desta loja.'
  if (reason === 'pending-month-selection') return 'Selecione um mês antes de prosseguir.'

  return 'A loja não está disponível para criação.'
}

const buildEligibility = (reason: StoreEligibilityReason): StoreEligibility => ({
  canSelect: reason === 'available',
  canSubmit: reason === 'available',
  reason
})
